import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import { TrackingScripts, WhatsAppFloat } from "@/components/Marketing";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import ProductPage from "@/pages/ProductPage";
import EMIPage from "@/pages/EMIPage";
import SavingsPage from "@/pages/SavingsPage";
import EligibilityPage from "@/pages/EligibilityPage";
import PrepaymentPage from "@/pages/PrepaymentPage";
import ApplyPage from "@/pages/ApplyPage";
import BlogList from "@/pages/BlogList";
import BlogArticle from "@/pages/BlogArticle";
import WPPage from "@/pages/WPPage";
import About from "@/pages/About";
import PartnersPage from "@/pages/PartnersPage";
import CardCategoryPage from "@/pages/CardCategoryPage";
import CardFinderPage from "@/pages/CardFinderPage";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AccountPage from "@/pages/AccountPage";
import LoanComparePage from "@/pages/LoanComparePage";
import BusinessLoanPage from "@/pages/BusinessLoanPage";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !hash.includes("=")) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
          else el.scrollIntoView({ behavior: "smooth" });
        }
      }, 400);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SiteLayout({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-ivory">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-40 text-center" data-testid="not-found-page">
      <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">404</p>
      <h1 className="mt-4 font-serif text-5xl font-semibold text-concordia">This page took a loan and vanished.</h1>
      <p className="mt-4 text-sm text-slate">The page you're looking for doesn't exist or has moved.</p>
      <Link to="/" data-testid="not-found-home-link" className="mt-8 inline-block rounded-full bg-concordia px-8 py-3.5 text-sm font-semibold text-ivory">
        Back to home
      </Link>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth returns with #session_id=... — exchange it before any route renders.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <SiteLayout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/loans/credit-cards/:category" element={<CardCategoryPage />} />
        <Route path="/loans/business-loan" element={<BusinessLoanPage />} />
        <Route path="/card-finder" element={<CardFinderPage />} />
        <Route path="/loans/:slug" element={<ProductPage />} />
        <Route path="/emi-calculator" element={<EMIPage />} />
        <Route path="/savings-calculator" element={<SavingsPage />} />
        <Route path="/eligibility-calculator" element={<EligibilityPage />} />
        <Route path="/prepayment-calculator" element={<PrepaymentPage />} />
        <Route path="/apply" element={<ApplyPage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/p/:slug" element={<WPPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/loan-comparison" element={<LoanComparePage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </SiteLayout>
  );
}

function App() {
  useLenis();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollManager />
          <AppRouter />
          <TrackingScripts />
          <WhatsAppFloat />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
